import React, {
  FunctionComponent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AnalyticsContextType,
  AnalyticsProviderProps,
  InstancesStore,
} from './AnalyticsContext.types';
import { EventParams } from '../Event.types';
import {
  getEventBucket,
  clearEventBucket,
  EventBucketData,
  appendToEventBucket,
} from '../../EventBucket';
import {
  MAX_EVENT_NAME_LENGTH,
  MAX_COMPONENT_LOCATION_LENGTH,
  MAX_COMPONENT_TEXT_LENGTH,
} from '../Analytics.constants';
import { toPascalCase } from '../../../utils/toPascalCase';
import { setMaxTextLength } from '../../../utils/setMaxTextLength';
import { handleEventSending } from '../utils/sendingEvents';
import { getAnalyticsInstances } from '../utils/initializeInstance';
import { setDefaultEventParams } from '../utils/defaultEventParams';

const AnalyticsContext = createContext<AnalyticsContextType>({});

export const AnalyticsProvider: FunctionComponent<AnalyticsProviderProps> = props => {
  const { children, appDataSync } = props;
  const {
    pathname,
    user,
    devEnvironment,
    userVariant = 'paciente',
    audience = 'web',
  } = appDataSync;

  const [isEnabled, setIsEnabled] = useState(false);
  const [hasUserId, setHasUserId] = useState(false);
  const [projectInstance, setProjectInstance] = useState<InstancesStore>({});
  const [instances, setInstances] = useState<InstancesStore>({});

  const hasInstances = useMemo(() => Object.keys(instances).length, [
    instances,
  ]);

  const autoFormatEventName = useCallback(
    (value: Event | string): string => {
      const firstPath = pathname?.split('/')[1] || '';
      const textContent =
        typeof value === 'string'
          ? (value as string)
          : (value?.target as HTMLElement)?.textContent || 'Undefined';

      const componentLocation = (toPascalCase(firstPath) || 'Home').slice(
        0,
        setMaxTextLength(
          MAX_COMPONENT_TEXT_LENGTH,
          textContent,
          MAX_COMPONENT_LOCATION_LENGTH
        )
      );

      const componentText = toPascalCase(textContent).slice(
        0,
        setMaxTextLength(
          MAX_COMPONENT_LOCATION_LENGTH,
          componentLocation,
          MAX_COMPONENT_TEXT_LENGTH
        )
      );
      const userType = userVariant === 'profissional' ? 'Pro' : 'Pac';
      const result = [userType, componentLocation, componentText].join('_');

      return result;
    },
    [pathname]
  );

  const sendAnalyticsEvent = useCallback(
    (eventName: string | Event, eventParams: EventParams = {}): void => {
      if (!eventName) return;

      const formattedEventName =
        typeof eventName === 'string'
          ? eventName
          : autoFormatEventName(eventName);
      const slicedEventName = formattedEventName.slice(
        0,
        MAX_EVENT_NAME_LENGTH
      );
      const finalEventName =
        eventName instanceof Object ? formattedEventName : slicedEventName;

      const debug_mode = devEnvironment
        ? { options: { debug_mode: true } }
        : {};
      const params = setDefaultEventParams(
        { user, userVariant, audience, path: pathname },
        { ...debug_mode, ...eventParams }
      );

      if (!hasInstances) {
        return appendToEventBucket(finalEventName, eventParams);
      }

      handleEventSending(instances, devEnvironment, finalEventName, params);
    },
    [instances, autoFormatEventName, devEnvironment, user]
  );

  const configUserId = useCallback(() => {
    if (user && !hasUserId) {
      sendAnalyticsEvent('Setting_User_Id');
      setHasUserId(true);
    }

    if (!user && hasUserId) {
      setHasUserId(false);
    }
  }, [user, sendAnalyticsEvent, hasUserId]);

  useEffect(() => {
    if (!isEnabled) {
      const analyticsInstances = getAnalyticsInstances(appDataSync);
      setInstances(analyticsInstances);
      setIsEnabled(true);
    }

    const eventBucket = getEventBucket();
    if (hasInstances && eventBucket?.length) {
      eventBucket.forEach(({ eventName, eventParams }: EventBucketData) =>
        sendAnalyticsEvent(eventName, eventParams)
      );
      clearEventBucket();
    }

    configUserId();
  }, [instances, setInstances, user]);

  useEffect(() => {
    if (projectInstance) {
      setInstances(currentValue => ({
        ...currentValue,
        ...(projectInstance as InstancesStore),
      }));
    }
  }, [projectInstance]);

  const providerValue = useMemo(
    () => ({
      sendAnalyticsEvent,
      autoFormatEventName,
      projectInstance,
      setProjectInstance,
      instances,
    }),
    [
      sendAnalyticsEvent,
      autoFormatEventName,
      projectInstance,
      setProjectInstance,
      instances,
    ]
  );

  return (
    <AnalyticsContext.Provider value={providerValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('AnalyticsContext must be used within a AnalyticsProvider');
  }
  return context;
};
